import { useQuery, keepPreviousData } from '@tanstack/react-query';
import deviceAnalyticsService from '../lib/device-analytics.service';
import type { DeviceMetricsParams, DeviceEventsParams, DeviceCadenceParams } from '../types';

// Query keys
export const deviceAnalyticsKeys = {
  all: ['device-analytics'] as const,
  metrics: (deviceId: string, params?: DeviceMetricsParams) =>
    [...deviceAnalyticsKeys.all, 'metrics', deviceId, params] as const,
  events: (deviceId: string, params?: DeviceEventsParams) =>
    [...deviceAnalyticsKeys.all, 'events', deviceId, params] as const,
  cadence: (deviceId: string, params?: DeviceCadenceParams) =>
    [...deviceAnalyticsKeys.all, 'cadence', deviceId, params] as const,
};

// Fetch time-series metrics for a device
export function useDeviceMetrics(
  deviceId: string,
  params?: DeviceMetricsParams,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: deviceAnalyticsKeys.metrics(deviceId, params),
    queryFn: () => deviceAnalyticsService.getDeviceMetrics(deviceId, params),
    enabled: !!deviceId && (options?.enabled ?? true),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });
}

// Fetch heartbeat cadence (beat counts, missed beats, gaps) for a device
export function useDeviceHeartbeatCadence(
  deviceId: string,
  params?: DeviceCadenceParams,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: deviceAnalyticsKeys.cadence(deviceId, params),
    queryFn: () => deviceAnalyticsService.getDeviceHeartbeatCadence(deviceId, params),
    enabled: !!deviceId && (options?.enabled ?? true),
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });
}

// Fetch device events
export function useDeviceEvents(
  deviceId: string,
  params?: DeviceEventsParams
) {
  return useQuery({
    queryKey: deviceAnalyticsKeys.events(deviceId, params),
    queryFn: () => deviceAnalyticsService.getDeviceEvents(deviceId, params),
    enabled: !!deviceId,
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  });
}
